import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAdminUsers, useApartments } from "@/hooks/use-resources";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, Play, RefreshCw } from "lucide-react";
import type { RecurringCharge } from "@shared/schema";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function formatMoney(v: string | number | null | undefined) {
  return `$${Number(v || 0).toFixed(2)}`;
}

function formatDate(d: string | Date | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function scheduleLabel(charge: RecurringCharge) {
  if (charge.frequency === "monthly") return `Monthly on day ${charge.dayOfMonth}`;
  const days = charge.daysOfWeek && charge.daysOfWeek.length > 0
    ? charge.daysOfWeek
    : charge.dayOfWeek != null ? [charge.dayOfWeek] : [];
  const dayLabel = days.length > 0 ? days.map(d => DAY_NAMES[d]).join(", ") : "—";
  if (charge.frequency === "biweekly") return `Every other week on ${dayLabel}`;
  return `Weekly on ${dayLabel}`;
}

type BillingTarget = "pm" | "org_expense";

type FormState = {
  billingTarget: BillingTarget;
  ownerUserId: string;
  payeeUserId: string;
  apartmentId: string;
  amount: string;
  description: string;
  category: string;
  frequency: "monthly" | "weekly" | "biweekly";
  dayOfMonth: string;
  daysOfWeek: number[];
  startsAt: string;
  endsAt: string;
};

// Categories shown per direction. PM = billed to the property manager (income).
// Org expense = absorbed by the org (reduces admin commission).
const PM_CATEGORIES = [
  { value: "miscellaneous", label: "Miscellaneous" },
  { value: "storage", label: "Storage" },
  { value: "other", label: "Other" },
];
const ORG_EXPENSE_CATEGORIES = [
  { value: "salary", label: "Salary / Contractor" },
  { value: "utility_water", label: "Water" },
  { value: "utility_electric", label: "Electricity" },
  { value: "utility_gas", label: "Gas" },
  { value: "other", label: "Other" },
];

const EMPTY_FORM: FormState = {
  billingTarget: "pm",
  ownerUserId: "none",
  payeeUserId: "none",
  apartmentId: "none",
  amount: "",
  description: "",
  category: "miscellaneous",
  frequency: "monthly",
  dayOfMonth: "28",
  daysOfWeek: [1], // Monday by default
  startsAt: "",
  endsAt: "",
};

export function RecurringChargesTab() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: usersData = [] } = useAdminUsers();
  const { data: apartments = [] } = useApartments();

  const { data: chargesRaw = [], isLoading } = useQuery<RecurringCharge[]>({
    queryKey: ["/api/admin/recurring-charges"],
  });
  const charges = chargesRaw as RecurringCharge[];

  const typedUsers = usersData as Array<{ id: number; fullName?: string; username?: string; role: string }>;
  const typedApts = apartments as Array<{ id: number; name: string }>;

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<RecurringCharge | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editForm, setEditForm] = useState<FormState>(EMPTY_FORM);
  const [deleteConfirmCharge, setDeleteConfirmCharge] = useState<RecurringCharge | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/recurring-charges"] });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await apiRequest("POST", "/api/admin/recurring-charges", data);
      return res.json() as Promise<{ backfilledCount?: number }>;
    },
    onSuccess: (result) => {
      invalidate();
      // Backfill creates financial records for the past periods — refresh those views too.
      queryClient.invalidateQueries({ queryKey: ["/api/admin/financial-records"] });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      const n = result?.backfilledCount ?? 0;
      toast({ title: n > 0 ? `Recurring charge created — ${n} past period${n === 1 ? "" : "s"} backfilled` : "Recurring charge created" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Record<string, unknown> }) =>
      apiRequest("PATCH", `/api/admin/recurring-charges/${id}`, data),
    onSuccess: () => { invalidate(); setEditOpen(false); toast({ title: "Recurring charge updated" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/recurring-charges/${id}`),
    onSuccess: () => { invalidate(); toast({ title: "Recurring charge deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const runMutation = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/admin/recurring-charges/${id}/run`),
    onSuccess: () => { invalidate(); queryClient.invalidateQueries({ queryKey: ["/api/admin/financial-records"] }); toast({ title: "Charge executed — financial record created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const buildPayload = (f: FormState) => {
    const isOrgExpense = f.billingTarget === "org_expense";
    return {
      billingTarget: f.billingTarget,
      // An org expense is never billed to a PM; a PM charge has no payee.
      ownerUserId: !isOrgExpense && f.ownerUserId !== "none" ? Number(f.ownerUserId) : null,
      payeeUserId: isOrgExpense && f.payeeUserId !== "none" ? Number(f.payeeUserId) : null,
      apartmentId: f.apartmentId !== "none" ? Number(f.apartmentId) : null,
      amount: f.amount,
      description: f.description,
      category: f.category,
      frequency: f.frequency,
      dayOfMonth: f.frequency === "monthly" ? Number(f.dayOfMonth) : null,
      dayOfWeek: null,
      daysOfWeek: (f.frequency === "weekly" || f.frequency === "biweekly") ? f.daysOfWeek : null,
      // Past start backfills the missed periods (server-side); future start anchors the first run.
      startsAt: f.startsAt ? new Date(`${f.startsAt}T00:00:00`).toISOString() : null,
      endsAt: f.endsAt ? new Date(`${f.endsAt}T23:59:59`).toISOString() : null,
    };
  };

  const validate = (f: FormState): string | null => {
    if (!f.amount || !f.description) return "Amount and description are required";
    if (f.frequency === "monthly" && !f.dayOfMonth) return "Day of month is required for monthly";
    if ((f.frequency === "weekly" || f.frequency === "biweekly") && f.daysOfWeek.length === 0) {
      return "Pick at least one day of week";
    }
    return null;
  };

  const handleCreate = () => {
    const err = validate(form);
    if (err) { toast({ title: "Error", description: err, variant: "destructive" }); return; }
    createMutation.mutate(buildPayload(form));
  };

  const handleEdit = (charge: RecurringCharge) => {
    setEditingCharge(charge);
    setEditForm({
      billingTarget: (charge.billingTarget as BillingTarget) ?? "pm",
      ownerUserId: charge.ownerUserId ? String(charge.ownerUserId) : "none",
      payeeUserId: charge.payeeUserId ? String(charge.payeeUserId) : "none",
      apartmentId: charge.apartmentId ? String(charge.apartmentId) : "none",
      amount: charge.amount,
      description: charge.description,
      category: charge.category,
      frequency: (charge.frequency as FormState["frequency"]) ?? "monthly",
      dayOfMonth: charge.dayOfMonth ? String(charge.dayOfMonth) : "28",
      daysOfWeek: charge.daysOfWeek && charge.daysOfWeek.length > 0
        ? charge.daysOfWeek
        : (charge.dayOfWeek != null ? [charge.dayOfWeek] : [1]),
      startsAt: charge.startsAt ? (new Date(charge.startsAt).toISOString().split("T")[0] ?? "") : "",
      endsAt: charge.endsAt ? (new Date(charge.endsAt).toISOString().split("T")[0] ?? "") : "",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingCharge) return;
    const err = validate(editForm);
    if (err) { toast({ title: "Error", description: err, variant: "destructive" }); return; }
    updateMutation.mutate({ id: editingCharge.id, data: buildPayload(editForm) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold">Recurring Charges</h2>
          <p className="text-sm text-muted-foreground">
            Automatic periodic charges that generate financial records without a cleaning job.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setForm(EMPTY_FORM)}>
              <Plus className="w-4 h-4 mr-2" />
              New Recurring Charge
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Recurring Charge</DialogTitle>
            </DialogHeader>
            <ChargeForm
              f={form}
              setF={setForm}
              users={typedUsers}
              apartments={typedApts}
              onSubmit={handleCreate}
              loading={createMutation.isPending}
              submitLabel="Create Charge"
            />
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : charges.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            No recurring charges configured. Use "New Recurring Charge" to add one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {charges.map((charge) => (
            <Card key={charge.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{charge.description}</span>
                      <Badge variant={charge.active ? "default" : "secondary"}>
                        {charge.active ? "Active" : "Paused"}
                      </Badge>
                      <Badge variant="outline">{charge.category}</Badge>
                      {charge.billingTarget === "org_expense" && (
                        <Badge variant="outline" className="text-red-700 border-red-300">Org Expense</Badge>
                      )}
                    </div>
                    <p className={`text-lg font-bold ${charge.billingTarget === "org_expense" ? "text-red-700" : "text-green-700"}`}>
                      {charge.billingTarget === "org_expense" ? `– ${formatMoney(charge.amount)}` : formatMoney(charge.amount)}
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{scheduleLabel(charge)}</span>
                      <span>Next: {formatDate(charge.nextRunAt)}</span>
                      {charge.lastRunAt && <span>Last run: {formatDate(charge.lastRunAt)}</span>}
                      {charge.endsAt && <span>Ends: {formatDate(charge.endsAt)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runMutation.mutate(charge.id)}
                      disabled={runMutation.isPending}
                      title="Run now — creates a financial record immediately"
                    >
                      {runMutation.isPending ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Run now</span>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(charge)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteConfirmCharge(charge)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Recurring Charge</DialogTitle>
          </DialogHeader>
          <ChargeForm
            f={editForm}
            setF={setEditForm}
            users={typedUsers}
            apartments={typedApts}
            onSubmit={handleSaveEdit}
            loading={updateMutation.isPending}
            submitLabel="Save Changes"
            showActiveToggle
            active={editingCharge?.active ?? true}
            onActiveChange={(v) => setEditingCharge((prev) => prev ? { ...prev, active: v } : prev)}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteConfirmCharge !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmCharge(null); }}
        title="Delete this recurring charge?"
        description={deleteConfirmCharge ? <>This will permanently delete the recurring charge <strong>"{deleteConfirmCharge.description || 'untitled'}"</strong> ({formatMoney(deleteConfirmCharge.amount)} — {scheduleLabel(deleteConfirmCharge)}). Financial records already generated by this charge will <strong>not</strong> be affected — only the schedule stops. This cannot be undone.</> : null}
        confirmLabel="Delete Recurring Charge"
        pending={deleteMutation.isPending}
        onConfirm={() => { if (deleteConfirmCharge) { deleteMutation.mutate(deleteConfirmCharge.id); setDeleteConfirmCharge(null); } }}
      />
    </div>
  );
}

// ── Shared form ─────────────────────────────────────────────────────────────

interface ChargeFormProps {
  f: FormState;
  setF: React.Dispatch<React.SetStateAction<FormState>>;
  users: Array<{ id: number; fullName?: string; username?: string; role: string }>;
  apartments: Array<{ id: number; name: string }>;
  onSubmit: () => void;
  loading: boolean;
  submitLabel: string;
  showActiveToggle?: boolean;
  active?: boolean;
  onActiveChange?: (v: boolean) => void;
}

function ChargeForm({ f, setF, users, apartments, onSubmit, loading, submitLabel, showActiveToggle, active, onActiveChange }: ChargeFormProps) {
  const toggleDay = (i: number) => {
    setF(p => {
      const days = p.daysOfWeek.includes(i) ? p.daysOfWeek.filter(d => d !== i) : [...p.daysOfWeek, i].sort((a, b) => a - b);
      return { ...p, daysOfWeek: days };
    });
  };

  const isOrgExpense = f.billingTarget === "org_expense";
  const categories = isOrgExpense ? ORG_EXPENSE_CATEGORIES : PM_CATEGORIES;

  // When switching direction, snap the category to a valid option for that direction.
  const setBillingTarget = (target: BillingTarget) => {
    setF((p) => {
      const opts = target === "org_expense" ? ORG_EXPENSE_CATEGORIES : PM_CATEGORIES;
      const stillValid = opts.some((o) => o.value === p.category);
      return { ...p, billingTarget: target, category: stillValid ? p.category : (opts[0]?.value ?? "other") };
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Charge to</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <Button
            type="button"
            variant={!isOrgExpense ? "default" : "outline"}
            className="h-auto py-2 flex-col items-start text-left"
            onClick={() => setBillingTarget("pm")}
            data-testid="button-billing-target-pm"
          >
            <span className="font-medium">Property Manager</span>
            <span className="text-[10px] opacity-80 font-normal">Billed to the PM (income)</span>
          </Button>
          <Button
            type="button"
            variant={isOrgExpense ? "default" : "outline"}
            className="h-auto py-2 flex-col items-start text-left"
            onClick={() => setBillingTarget("org_expense")}
            data-testid="button-billing-target-org"
          >
            <span className="font-medium">Org Expense</span>
            <span className="text-[10px] opacity-80 font-normal">Absorbed by org (reduces profit)</span>
          </Button>
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Input value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} placeholder={isOrgExpense ? "Electricity — Unit 4B" : "Storage fee — Unit 4B"} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount ($)</Label>
          <Input type="number" step="0.01" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} placeholder="0.00" className={isOrgExpense ? "text-red-700" : "text-green-700"} />
        </div>
        <div>
          <Label>Category</Label>
          <Select value={f.category} onValueChange={(v) => setF((p) => ({ ...p, category: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Frequency</Label>
        <Select value={f.frequency} onValueChange={(v) => setF((p) => ({ ...p, frequency: v as FormState["frequency"] }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="biweekly">Every other week</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {f.frequency === "monthly" ? (
        <div>
          <Label>Day of Month (1–31)</Label>
          <Input type="number" min={1} max={31} value={f.dayOfMonth} onChange={(e) => setF((p) => ({ ...p, dayOfMonth: e.target.value }))} />
        </div>
      ) : (
        <div>
          <Label>Days of week (pick one or more)</Label>
          <div className="flex gap-1.5 flex-wrap mt-1">
            {DAY_NAMES_FULL.map((d, i) => {
              const selected = f.daysOfWeek.includes(i);
              return (
                <Button
                  key={i}
                  type="button"
                  size="sm"
                  variant={selected ? "default" : "outline"}
                  className="h-9 w-14 text-xs"
                  onClick={() => toggleDay(i)}
                  data-testid={`button-charge-day-${i}`}
                >
                  {DAY_NAMES[i]}
                </Button>
              );
            })}
          </div>
          {f.frequency === "biweekly" && (
            <p className="text-[10px] text-muted-foreground mt-1">
              Charge fires every other week on the selected day(s).
            </p>
          )}
        </div>
      )}
      <div>
        <Label>Starts on (optional)</Label>
        <Input
          type="date"
          value={f.startsAt}
          onChange={(e) => setF((p) => ({ ...p, startsAt: e.target.value }))}
          data-testid="input-charge-starts-at"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          A past date backfills every missed period when the charge is created (up to the end date, or today). Leave blank to start today.
        </p>
      </div>
      <div>
        <Label>Ends on (optional)</Label>
        <Input
          type="date"
          value={f.endsAt}
          onChange={(e) => setF((p) => ({ ...p, endsAt: e.target.value }))}
          data-testid="input-charge-ends-at"
        />
        <p className="text-[10px] text-muted-foreground mt-1">Leave blank to keep charging indefinitely.</p>
      </div>
      {isOrgExpense ? (
        <div>
          <Label>Payee — contractor paid (optional)</Label>
          <SearchableSelect
            value={f.payeeUserId}
            onValueChange={(v) => setF((p) => ({ ...p, payeeUserId: v }))}
            placeholder="None"
            searchPlaceholder="Search user..."
            options={[
              { value: "none", label: "None" },
              ...users.filter(u => (u as any).isActive !== false).map(u => ({ value: String(u.id), label: `${u.fullName || u.username || String(u.id)}${u.role ? ` (${u.role})` : ""}` })),
            ]}
            className="w-full"
          />
          <p className="text-[10px] text-muted-foreground mt-1">For a salary, pick the contractor — the payment is also recorded on their 1099.</p>
        </div>
      ) : (
        <div>
          <Label>Property Manager</Label>
          <SearchableSelect
            value={f.ownerUserId}
            onValueChange={(v) => setF((p) => ({ ...p, ownerUserId: v }))}
            placeholder="None"
            searchPlaceholder="Search PM..."
            options={[
              { value: "none", label: "None" },
              ...users.filter(u => u.role === "owner" && (u as any).isActive !== false).map(u => ({ value: String(u.id), label: u.fullName || u.username || String(u.id) })),
            ]}
            className="w-full"
          />
          <p className="text-[10px] text-muted-foreground mt-1">The PM this charge is billed to (appears on their invoice).</p>
        </div>
      )}
      <div>
        <Label>Apartment (optional)</Label>
        <SearchableSelect
          value={f.apartmentId}
          onValueChange={(v) => setF((p) => ({ ...p, apartmentId: v }))}
          placeholder="None"
          searchPlaceholder="Search apartment..."
          options={[
            { value: "none", label: "None" },
            ...apartments.map(a => ({ value: String(a.id), label: a.name })),
          ]}
          className="w-full"
        />
      </div>
      {showActiveToggle && (
        <div className="flex items-center gap-2">
          <input
            id="active-toggle"
            type="checkbox"
            className="h-4 w-4"
            checked={active ?? true}
            onChange={(e) => onActiveChange?.(e.target.checked)}
          />
          <Label htmlFor="active-toggle">Active</Label>
        </div>
      )}
      <Button className="w-full" onClick={onSubmit} disabled={loading}>
        {loading ? "Saving..." : submitLabel}
      </Button>
    </div>
  );
}
